// HoverPanelMockup.jsx
// -----------------------------------------------------------------------------
// Presentational character-context hover panel. Takes a `characterContext`
// prop (shape: see mockCharacters.js) and renders the 8-section panel using
// the editorial-profile + streaming-palette aesthetic from the design doc.
//
// Pure render. No data fetching. No server calls. Palette + glyph lookups
// are local module imports. Designed so that swapping in a real
// resolver-fed prop later is a one-line change at the call site.
//
// Spec reference: docs/journal/PHASE_4D_VA_HOVER_PANEL_VISUAL_DESIGN_MOCKUP.md
//
// Two interactive additions over the static mockup (2026-05-17):
//   1. SpeakersBars — a compact bar list under the Speaks-with sentence,
//      hidden when speakerContext has fewer than 2 names. Quick visual
//      cue alongside the prose template.
//   2. Chapter dialogue popup — clicking a chapter region on the sparkline
//      opens a floating card with 2-3 sample dialogue lines from that
//      chapter, attributed to interlocutors. Closes on ×, click-outside, or
//      Esc. No audio affordance (operator decision, same session).
// -----------------------------------------------------------------------------

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { resolveDualTone, getPalette, pickDrivingAffect } from './affectPalette';
import { getMotifGlyph, getBackgroundMotif } from './motifGlyphs';
import IconCloseButton from '../IconCloseButton';
import './mockup.css';

// Maximum visible character count for a single sampled dialogue line in the
// Chapter Activity popup. Anything longer is truncated with an ellipsis;
// the goal is "enough context to taste the voice," not "the whole speech."
// Per-line cap (not aggregated across the popup) — each line is evaluated
// independently so a short opener isn't penalised by a long follow-up.
// Tunable from this one place.
const CHAPTER_DIALOGUE_LINE_MAX_CHARS = 500;

// Truncate a dialogue line at CHAPTER_DIALOGUE_LINE_MAX_CHARS with an
// ellipsis, snapping the cut back to the nearest word boundary so we
// don't sever a word mid-character. Strategy:
//   1. Slice at the hard cap.
//   2. Find the last run of whitespace inside that slice and cut there.
//   3. If no whitespace exists in the first MAX_CHARS (single absurdly
//      long token, e.g. a pasted URL), fall back to the hard slice — a
//      "least bad" outcome that still respects the cap.
//   4. trimEnd() any trailing whitespace/punctuation gap so "word …" and
//      "word, …" don't get a doubled space before the ellipsis.
// Null/empty pass through untouched.
const truncateDialogueLine = (text) => {
  if (!text || text.length <= CHAPTER_DIALOGUE_LINE_MAX_CHARS) return text;
  const hardSlice = text.slice(0, CHAPTER_DIALOGUE_LINE_MAX_CHARS);
  // Last whitespace index in the hard-sliced window — that's our word
  // boundary. We use a regex so a run of whitespace (rare in dialogue but
  // not impossible) still resolves to the start of that run.
  const lastSpace = hardSlice.search(/\s+\S*$/);
  const boundary = lastSpace > 0 ? hardSlice.slice(0, lastSpace) : hardSlice;
  return `${boundary.trimEnd()}…`;
};



// ---------------------------------------------------------------------------
// SpeakersBars — three (or two) short labelled rows under the speaks-with
// sentence. Reads "Cambitter ──────── 42%" per row. Hidden entirely when
// speakerContext has fewer than 2 names.
// ---------------------------------------------------------------------------
const SpeakersBars = ({ rows }) => {
  if (!rows || rows.length < 2) return null;
  const top = rows.slice(0, 3);
  // Normalise to the largest row's share so the visible bar width is
  // proportional WITHIN the top-3, not as a fraction of 100% — otherwise
  // the bars all look tiny when shares sum to ~70%.
  const maxShare = Math.max(...top.map((r) => r.share));

  return (
    <ul className="aai-cc-speakers">
      {top.map((r) => {
        const fillPct = Math.round((r.share / maxShare) * 100);
        return (
          <li className="aai-cc-speakers__row" key={r.name}>
            <span className="aai-cc-speakers__name">{r.name}</span>
            <span className="aai-cc-speakers__bar" aria-hidden>
              <span
                className="aai-cc-speakers__bar-fill"
                style={{ width: `${fillPct}%` }}
              />
            </span>
            <span className="aai-cc-speakers__share">
              {Math.round(r.share * 100)}%
            </span>
          </li>
        );
      })}
    </ul>
  );
};

// ---------------------------------------------------------------------------
// ChapterDialogPopup — floating card overlaid on the panel surface, anchored
// to the top of the panel, showing 2-3 dialogue lines from the clicked
// chapter. Uses the same visual vocabulary as the voice-excerpt quote (serif
// italics, accent left-border, soft-mix background).
// ---------------------------------------------------------------------------
const ChapterDialogPopup = ({ chapterRow, onClose }) => {
  if (!chapterRow) return null;
  const lines = chapterRow.sampleLines || [];

  return (
    <div className="aai-cc-chapter-popup" role="dialog" aria-label={`Chapter ${chapterRow.chapter} dialogue sample`}>
      <header className="aai-cc-chapter-popup__head">
        <p className="aai-cc-chapter-popup__eyebrow">
          Chapter {chapterRow.chapter} · {chapterRow.lineCount} lines
        </p>
        {/* Close affordance — shared visual treatment lives in the
         * IconCloseButton component (flat × at rest, accent-tinted pill on
         * hover/focus, focus-ring suppression baked in). */}
        <IconCloseButton
          onClick={onClose}
          ariaLabel="Close chapter dialogue sample"
        />

      </header>

      <div className="aai-cc-chapter-popup__body">
        {lines.length === 0 ? (
          <p className="aai-cc-chapter-popup__empty">
            (No sample lines available for this chapter.)
          </p>
        ) : (
          lines.map((l, i) => (
            <div className="aai-cc-chapter-popup__line-block" key={i}>
              <p className="aai-cc-chapter-popup__line">
                <span className="aai-cc-chapter-popup__qmark" aria-hidden>
                  “
                </span>
                {truncateDialogueLine(l.line)}
                <span className="aai-cc-chapter-popup__qmark" aria-hidden>
                  ”
                </span>
              </p>

              {l.toName && (
                <p className="aai-cc-chapter-popup__attribution">
                  — to {l.toName}
                </p>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sparkline — flowing curve over chapter-presence lineCounts.
// Acts as both data viz and decoration (per design doc §3 shape language).
// Now interactive: hovering a region highlights it, clicking opens the
// chapter dialogue popup.
// ---------------------------------------------------------------------------
const Sparkline = ({
  data,
  accent,
  totalChapters,
  onChapterClick,
  hoveredChapter,
  setHoveredChapter,
  openChapter,
}) => {
  if (!data || data.length === 0) return null;

  const width = 300;
  const height = 56;
  const padX = 6;

  // Chapter span for the curve: prefer the book's chapter count, but never below the highest chapter
  // present in the data. Guards books whose lines carry no chapter numbers (chapterNumber defaults to 0
  // at ingest), where totalChapters arrives 0/undefined → an empty series → `points[0]` crash.
  const nChapters = Math.max(Number(totalChapters) || 0, ...data.map((d) => Number(d.chapter) || 0));
  if (nChapters < 1) return null;

  // Build a dense series across ALL chapters so the curve has a true zero
  // valley wherever the character is absent.
  const series = [];
  for (let ch = 1; ch <= nChapters; ch++) {
    const hit = data.find((d) => d.chapter === ch);
    series.push(hit ? { chapter: ch, lineCount: hit.lineCount, row: hit } : { chapter: ch, lineCount: 0, row: null });
  }
  const maxVal = Math.max(...series.map((s) => s.lineCount), 1);
  const stepX = (width - 2 * padX) / Math.max(series.length - 1, 1);

  const points = series.map((s, i) => {
    const x = padX + i * stepX;
    const y = height - 4 - (s.lineCount / maxVal) * (height - 10);
    return { x, y, chapter: s.chapter, row: s.row };
  });

  // Smooth path
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];
    const mx = (p1.x + p2.x) / 2;
    d += ` Q ${p1.x} ${p1.y}, ${mx} ${(p1.y + p2.y) / 2}`;
  }
  d += ` T ${points[points.length - 1].x} ${points[points.length - 1].y}`;

  const dArea = `${d} L ${points[points.length - 1].x} ${height} L ${points[0].x} ${height} Z`;

  // Hit-region width per chapter, in viewBox units. Used to draw the
  // transparent click/hover targets.
  const hitWidth = stepX;

  return (
    <div className="aai-cc-sparkline">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        className="aai-cc-sparkline__svg"
      >
        {/* Area fill */}
        <path d={dArea} fill={accent} opacity="0.13" />
        {/* Curve line. Visually the sparkline reads as decoration first,
         * data second — the area fill below already carries the shape
         * information, so the stroke just traces the upper boundary.
         * Kept hairline-thin (0.9) and semi-transparent (0.55) so it
         * doesn't compete with the editorial quote and dominant-affect
         * pills above; an earlier 1.6 / fully-opaque stroke read as a
         * heavy ink line and pulled the eye downward off-balance. */}
        <path
          d={d}
          fill="none"
          stroke={accent}
          strokeWidth="0.3"
          strokeLinecap="round"
          opacity="0.55"
        />


        {/* Highlight band for hovered or opened chapter */}
        {points.map((p) => {
          const isOpen = openChapter === p.chapter;
          const isHover = hoveredChapter === p.chapter && !isOpen;
          if (!isOpen && !isHover) return null;
          return (
            <line
              key={`hl-${p.chapter}`}
              x1={p.x}
              x2={p.x}
              y1={0}
              y2={height}
              stroke={accent}
              strokeWidth={isOpen ? 1.6 : 0.9}
              opacity={isOpen ? 0.6 : 0.35}
              pointerEvents="none"
            />
          );
        })}

        {/* Hit regions — one transparent rect per chapter, sized so the
            whole horizontal slice is clickable. preserveAspectRatio="none"
            means the SVG stretches to container width, so these widen
            with the panel. */}
        {points.map((p) => {
          const clickable = p.row && p.row.lineCount > 0 && p.row.sampleLines && p.row.sampleLines.length > 0;
          return (
            <rect
              key={`hit-${p.chapter}`}
              x={p.x - hitWidth / 2}
              y={0}
              width={hitWidth}
              height={height}
              fill="transparent"
              style={{ cursor: clickable ? 'pointer' : 'default' }}
              onMouseEnter={() => clickable && setHoveredChapter(p.chapter)}
              onMouseLeave={() => clickable && setHoveredChapter(null)}
              onClick={() => clickable && onChapterClick(p.row)}
            />
          );
        })}
      </svg>

      {/* Floating chapter-hint tooltip above the hovered region */}
      {hoveredChapter && openChapter !== hoveredChapter && (() => {
        const p = points.find((pp) => pp.chapter === hoveredChapter);
        if (!p) return null;
        // Convert SVG x to a CSS percentage of the legend-row width.
        const leftPct = (p.x / width) * 100;
        const hit = series.find((s) => s.chapter === hoveredChapter);
        return (
          <div
            className="aai-cc-sparkline__hint"
            style={{ left: `${leftPct}%` }}
          >
            Ch {p.chapter} · {hit.lineCount} lines · click to read
          </div>
        );
      })()}

      <div className="aai-cc-sparkline__legend">
        <span>CH 1</span>
        <span>CHAPTER ACTIVITY</span>
        <span>CH {totalChapters}</span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// HoverPanelMockup — the panel itself.
// ---------------------------------------------------------------------------
const HoverPanelMockup = ({ characterContext }) => {
  const c = characterContext;

  // Local state for chapter-dialogue popup
  const [openChapter, setOpenChapter] = useState(null); // chapter number, or null
  const [hoveredChapter, setHoveredChapter] = useState(null);
  const surfaceRef = useRef(null);

  // Resolve palette. Thin-card characters get the neutral palette explicitly.
  const { primary, accentOverride } = c.isThinCard
    ? { primary: getPalette('neutral'), accentOverride: null }
    : resolveDualTone(c.dominantAffects);

  const accent = accentOverride || primary.accent;

  // CSS custom properties drive everything color-related downstream.
  const styleVars = {
    '--aai-cc-wash': primary.wash,
    '--aai-cc-surface': primary.surface,
    '--aai-cc-accent': accent,
  };

  // Motif: driving affect's glyph (or neutral 'circle' for thin-card).
  // `pickDrivingAffect` demotes a neutral [0] when a second row exists, so
  // the corner glyph reflects the character's actual flavour rather than
  // the corpus-baseline tint. Matches the demotion rule in
  // `resolveDualTone` above and the server-side rule in
  // `nlTemplates.renderAffectPhrase`.
  const driverRow = c.isThinCard ? null : pickDrivingAffect(c.dominantAffects);
  const motifKey = c.isThinCard
    ? 'circle'
    : (driverRow && getPalette(driverRow.emotion).glyph) || 'circle';
  const Motif = getMotifGlyph(motifKey);

  // Background motif: large decorative shape behind the panel content.
  // Suppressed for thin-card variants.
  const BackgroundMotif = c.isThinCard ? null : getBackgroundMotif('greenBlob');

  // Portrait initial.
  const initial = (c.name || '?').trim().charAt(0).toUpperCase();

  // The "voice in their own words" excerpt strips a single leading/trailing
  // pair of curly/straight quotes for cleaner display.
  const quoteText =
    c.voiceExcerpt &&
    c.voiceExcerpt.content
      .replace(/^["“]/, '')
      .replace(/["”]$/, '')
      .trim();

  // The currently-open chapter's full row (for the popup).
  const openChapterRow =
    openChapter == null
      ? null
      : c.chapterPresence.find((r) => r.chapter === openChapter) || null;

  // Handlers
  const handleChapterClick = useCallback(
    (row) => {
      setOpenChapter((current) => (current === row.chapter ? null : row.chapter));
    },
    []
  );
  const handleClosePopup = useCallback(() => setOpenChapter(null), []);

  // Esc closes the popup
  useEffect(() => {
    if (openChapter == null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpenChapter(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openChapter]);

  // Click outside the popup (but still inside the panel) closes it
  useEffect(() => {
    if (openChapter == null) return undefined;
    const onDown = (e) => {
      const surface = surfaceRef.current;
      if (!surface) return;
      // Only listen for clicks inside the surface that aren't on the popup
      // or on the sparkline (the sparkline manages its own toggle).
      if (!surface.contains(e.target)) return;
      const insidePopup = e.target.closest('.aai-cc-chapter-popup');
      const insideSparkline = e.target.closest('.aai-cc-sparkline');
      if (insidePopup || insideSparkline) return;
      setOpenChapter(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [openChapter]);

  return (
    <article
      className={`aai-cc-panel ${c.isThinCard ? 'aai-cc-panel--thin' : ''}`}
      style={styleVars}
      aria-label={`Character context for ${c.name}`}
    >
      <div
        className={`aai-cc-panel__surface ${openChapter != null ? 'aai-cc-panel__surface--popup-open' : ''}`}
        ref={surfaceRef}
      >
        {/* Background motif */}
        {BackgroundMotif && (
          <div className="aai-cc-panel__blob" aria-hidden>
            <BackgroundMotif color={accent} width={270} />
          </div>
        )}

        {/* Top-right corner motif glyph */}
        <div className="aai-cc-panel__motif">
          <Motif color={accent} size={120} />
        </div>

        {/* Header strip */}
        <header className="aai-cc-header">
          <div className="aai-cc-portrait" aria-hidden>
            {initial}
          </div>
          <div className="aai-cc-header__textblock">
            <h2 className="aai-cc-header__name">{c.name}</h2>
            <p className="aai-cc-header__role">
              {c.roleSize}
              <span className="aai-cc-header__role-detail">
                · {c.lineCount} lines · {c.chapterCount} ch
              </span>
            </p>
            {/* Book title — small, mixed-case, sits under the role line.
             * Resolver-overlaid so it always reflects the live opportunity's
             * book (when shown from a host page) and gracefully omits
             * itself for fixtures that don't carry one (the standalone
             * HoverPanelDemo case). */}
            {c.bookTitle && (
              <p className="aai-cc-header__book">{c.bookTitle}</p>
            )}
          </div>

        </header>

        {/* Headline NL sentence */}
        <p className="aai-cc-headline">{c.headlineNL}</p>

        {/* Speaks with */}
        {c.speakerContextSentence && (
          <section className="aai-cc-section">
            <p className="aai-cc-section__label">Speaks with</p>
            <p className="aai-cc-section__body">{c.speakerContextSentence}</p>
            <SpeakersBars rows={c.speakerContext} />
          </section>
        )}

        {/* Presence */}
        <section className="aai-cc-section">
          <p className="aai-cc-section__label">Presence</p>
          <p className="aai-cc-section__body">{c.presenceSentence}</p>
        </section>

        {/* In their own words */}
        {c.voiceExcerpt && (
          <blockquote className="aai-cc-quote">
            <span className="aai-cc-quote__mark" aria-hidden>
              “
            </span>
            <p className="aai-cc-quote__affect">In their own words · {c.voiceExcerpt.emotion}</p>
            <p className="aai-cc-quote__text">{quoteText}</p>
          </blockquote>
        )}

        {/* Dominant affects pill row */}
        {c.dominantAffects.length > 0 && (
          <section className="aai-cc-section">
            <p className="aai-cc-section__label">Dominant affects</p>
            <ul className="aai-cc-affects">
              {c.dominantAffects.map((a, i) => (
                <li
                  key={a.emotion}
                  className={`aai-cc-affects__pill ${i === 0 ? 'aai-cc-affects__pill--primary' : ''}`}
                >
                  <span className="aai-cc-affects__name">{a.emotion}</span>
                  <span className="aai-cc-affects__share">{Math.round(a.share * 100)}%</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* BC's note */}
        {c.bcNote && (
          <aside className="aai-cc-bcnote">
            <p className="aai-cc-bcnote__label">BC's note</p>
            <p className="aai-cc-bcnote__text">{c.bcNote}</p>
          </aside>
        )}

        {/* Chapter sparkline at the bottom */}
        {!c.isThinCard && (
          <Sparkline
            data={c.chapterPresence}
            accent={accent}
            totalChapters={c.bookTotalChapters}
            onChapterClick={handleChapterClick}
            hoveredChapter={hoveredChapter}
            setHoveredChapter={setHoveredChapter}
            openChapter={openChapter}
          />
        )}

        {/* Chapter dialogue popup — floats above panel content when open */}
        {openChapterRow && (
          <ChapterDialogPopup
            chapterRow={openChapterRow}
            onClose={handleClosePopup}
          />
        )}
      </div>
    </article>
  );
};

export default HoverPanelMockup;
